(function () {
  "use strict";

  const CONFIG = {
    MARKET: "US",
    PRODUCTS_PER_PAGE: 36,
    MAX_PAGES: 50,
    SORT_OPTIONS: {
      PRICE_ASC: "price-ascending",
      PRICE_DESC: "price-descending",
    },
  };

  const state = {
    isMarketPriceSortActive: false,
    isMarketPriceFilterActive: false,
    isReapplyingFilter: false,
    allProductsLoaded: false,
    allProducts: [],
    sortedProducts: [],
    filteredProducts: [],
    currentSortDirection: null,
    currentPriceMin: null,
    currentPriceMax: null,
    displayedCount: 0,
    originalGridHTML: null,
  };

  function isUSMarket() {
    const productGrid = document.getElementById("product-grid");
    if (productGrid && productGrid.dataset.market) {
      return productGrid.dataset.market === CONFIG.MARKET;
    }
    return window.currentCountryCode === CONFIG.MARKET;
  }

  function getProductsPerPage() {
    const paginateNext = document.getElementById("paginateNext");
    if (paginateNext && paginateNext.dataset.productsPerPage) {
      return (
        parseInt(paginateNext.dataset.productsPerPage, 10) ||
        CONFIG.PRODUCTS_PER_PAGE
      );
    }
    return CONFIG.PRODUCTS_PER_PAGE;
  }

  function isPriceSort(sortValue) {
    return (
      sortValue === CONFIG.SORT_OPTIONS.PRICE_ASC ||
      sortValue === CONFIG.SORT_OPTIONS.PRICE_DESC
    );
  }

  function showLoading() {
    const container = document.getElementById("ProductGridContainer");
    if (container) {
      const collection = container.querySelector(".collection");
      if (collection) {
        collection.classList.add("loading");
      }
    }

    const loadMoreBtn = document.querySelector(".load-more_btn");
    if (loadMoreBtn) {
      loadMoreBtn.style.display = "none";
    }
  }

  function hideLoading() {
    const container = document.getElementById("ProductGridContainer");
    if (container) {
      const collection = container.querySelector(".collection");
      if (collection) {
        collection.classList.remove("loading");
      }
    }
  }

  async function fetchPage(pageNum) {
    const url = new URL(window.location.href);
    url.searchParams.set("page", pageNum);
    url.searchParams.delete("sort_by");
    const productGrid = document.getElementById("product-grid");
    const sectionId = productGrid ? productGrid.dataset.id : null;
    if (sectionId) {
      url.searchParams.set("section_id", sectionId);
    }

    try {
      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.text();
    } catch (error) {
      console.error(`Error fetching page ${pageNum}:`, error);
      return null;
    }
  }

  function extractProductsFromHTML(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const grid = doc.getElementById("product-grid");

    if (!grid) return [];

    const products = [];
    const items = grid.querySelectorAll(".grid__item:not(.grid__item--cta)");

    items.forEach((item) => {
      const cardWrapper = item.querySelector(".card-wrapper");
      if (cardWrapper) {
        const marketPrice = parseFloat(cardWrapper.dataset.marketPrice) || 0;
        const productId = cardWrapper.dataset.productId;
        const productHandle = cardWrapper.dataset.productHandle;

        products.push({
          element: item.cloneNode(true),
          marketPrice: marketPrice,
          productId: productId,
          productHandle: productHandle,
        });
      }
    });

    return products;
  }

  async function fetchAllProducts() {
    const productGrid = document.getElementById("product-grid");
    const totalProducts = parseInt(
      productGrid?.dataset.totalProducts || "0",
      10
    );

    if (totalProducts === 0) {
      console.warn("No products count found");
      return [];
    }

    const productsPerPage = getProductsPerPage();
    const totalPages = Math.min(
      Math.ceil(totalProducts / productsPerPage),
      CONFIG.MAX_PAGES
    );

    console.log(
      `Fetching ${totalProducts} products across ${totalPages} pages (parallel)...`
    );

    const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

    const startTime = Date.now();
    const htmlResults = await Promise.all(
      pageNumbers.map((page) => fetchPage(page))
    );
    const fetchTime = Date.now() - startTime;
    console.log(`All pages fetched in ${fetchTime}ms`);

    const allProducts = [];
    const seenProductIds = new Set();

    htmlResults.forEach((html, index) => {
      if (!html) return;

      const products = extractProductsFromHTML(html);
      products.forEach((product) => {
        if (!seenProductIds.has(product.productId)) {
          seenProductIds.add(product.productId);
          allProducts.push(product);
        }
      });
    });

    console.log(`Processed ${allProducts.length} unique products`);
    return allProducts;
  }

  function sortProducts(products, direction) {
    return [...products].sort((a, b) => {
      if (direction === CONFIG.SORT_OPTIONS.PRICE_ASC) {
        return a.marketPrice - b.marketPrice;
      } else {
        return b.marketPrice - a.marketPrice;
      }
    });
  }

  /**
   * Filter products by price range
   */
  function filterProductsByPrice(products, minPrice, maxPrice) {
    return products.filter((product) => {
      // Convert cents to dollars for comparison (Shopify stores prices in cents)
      const priceInDollars = product.marketPrice / 100;

      if (minPrice !== null && priceInDollars < minPrice) {
        return false;
      }
      if (maxPrice !== null && priceInDollars > maxPrice) {
        return false;
      }
      return true;
    });
  }

  /**
   * Apply both filtering and sorting
   */
  function applyFiltersAndSort(products) {
    let result = [...products];

    // Apply price filter if active
    if (state.currentPriceMin !== null || state.currentPriceMax !== null) {
      result = filterProductsByPrice(
        result,
        state.currentPriceMin,
        state.currentPriceMax
      );
    }

    // Apply sort if active
    if (state.currentSortDirection) {
      result = sortProducts(result, state.currentSortDirection);
    }

    return result;
  }

  /**
   * Render sorted products to grid (paginated)
   * @param {Array} products - All sorted products
   * @param {number} count - Number of products to display
   * @param {boolean} append - If true, append to existing; if false, replace
   */

  function renderProducts(
    products,
    count = CONFIG.PRODUCTS_PER_PAGE,
    append = false
  ) {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    const startIndex = append ? state.displayedCount : 0;
    const endIndex = Math.min(startIndex + count, products.length);
    const productsToShow = products.slice(startIndex, endIndex);

    if (!append) {
      const ctaCards = productGrid.querySelectorAll(".grid__item--cta");
      state.ctaCards = Array.from(ctaCards).map((cta) => cta.cloneNode(true));

      productGrid.innerHTML = "";
      state.displayedCount = 0;
    }

    productsToShow.forEach((product, index) => {
      const element = product.element.cloneNode(true);
      if (element.style) {
        element.style.setProperty(
          "--animation-order",
          state.displayedCount + index + 1
        );
      }

      if (append) {
        element.classList.add("product-card-fade-in");
        element.style.animationDelay = `${index * 50}ms`;
      }

      productGrid.appendChild(element);
    });

    state.displayedCount = endIndex;

    updateLoadMoreButton(products.length);

    document.dispatchEvent(
      new CustomEvent("products:sorted", {
        detail: {
          count: state.displayedCount,
          total: products.length,
          hasMore: state.displayedCount < products.length,
        },
      })
    );

    if (typeof window.setupQuickAdd === "function") {
      setTimeout(() => window.setupQuickAdd(), 100);
    }
  }

  function updateLoadMoreButton(totalProducts) {
    const loadMoreBtn = document.querySelector(".load-more_btn");
    if (!loadMoreBtn) return;

    const hasMore = state.displayedCount < totalProducts;

    if (hasMore) {
      loadMoreBtn.style.display = "block";
      if (!loadMoreBtn.dataset.marketSortHandler) {
        loadMoreBtn.dataset.marketSortHandler = "true";
        loadMoreBtn.addEventListener("click", handleLoadMoreClick, true); // Capture phase
      }
    } else {
      loadMoreBtn.style.display = "none";
    }
  }

  function handleLoadMoreClick(event) {
    if (!state.isMarketPriceSortActive && !state.isMarketPriceFilterActive)
      return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (state.sortedProducts.length === 0) return;

    if (state.displayedCount >= state.sortedProducts.length) {
      console.log("No more products to display");
      return;
    }

    const btn = event.currentTarget;
    btn.setAttribute("data-loading", "");

    const perPage = getProductsPerPage();
    const remainingProducts =
      state.sortedProducts.length - state.displayedCount;
    const productsToLoad = Math.min(perPage, remainingProducts);

    showSkeletonCards(productsToLoad);

    setTimeout(() => {
      removeSkeletonCards();
      renderProducts(state.sortedProducts, perPage, true);
      btn.removeAttribute("data-loading");
      console.log(
        `Load More: showing ${state.displayedCount} of ${state.sortedProducts.length} products`
      );
    }, 400);
  }

  function showSkeletonCards(count) {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    for (let i = 0; i < count; i++) {
      const skeleton = document.createElement("li");
      skeleton.className = "grid__item skeleton-card";
      skeleton.innerHTML = `
        <div class="skeleton-card__inner">
          <div class="skeleton-card__image skeleton-animation"></div>
          <div class="skeleton-card__content">
            <div class="skeleton-card__title skeleton-animation"></div>
            <div class="skeleton-card__price skeleton-animation"></div>
          </div>
        </div>
      `;
      productGrid.appendChild(skeleton);
    }
  }

  function removeSkeletonCards() {
    const productGrid = document.getElementById("product-grid");
    if (!productGrid) return;

    const skeletons = productGrid.querySelectorAll(".skeleton-card");
    skeletons.forEach((skeleton) => skeleton.remove());
  }

  async function handleUSPriceSort(sortValue) {
    if (!isUSMarket() || !isPriceSort(sortValue)) {
      return false;
    }

    console.log("US Market price sort activated:", sortValue);
    showLoading();

    try {
      if (!state.allProductsLoaded || state.allProducts.length === 0) {
        console.log("Fetching all products for sorting...");
        state.allProducts = await fetchAllProducts();
        state.allProductsLoaded = true;
        console.log(`Loaded ${state.allProducts.length} products total`);
      }

      state.currentSortDirection = sortValue;

      // Apply both filter and sort
      state.sortedProducts = applyFiltersAndSort(state.allProducts);
      state.displayedCount = 0;

      const perPage = getProductsPerPage();
      renderProducts(state.sortedProducts, perPage, false);

      // Update product count if filtering is active
      if (state.isMarketPriceFilterActive) {
        updateProductCount(state.sortedProducts.length);
      }

      const url = new URL(window.location.href);
      url.searchParams.set("sort_by", sortValue);
      url.searchParams.set("us_price_sort", "true");
      window.history.replaceState({}, "", url.toString());

      state.isMarketPriceSortActive = true;

      console.log(
        `Displaying ${state.displayedCount} of ${state.sortedProducts.length} products (sorted by US price)`
      );
    } catch (error) {
      console.error("Error during US price sort:", error);
      return false;
    } finally {
      hideLoading();
    }

    return true;
  }

  function resetSort() {
    state.isMarketPriceSortActive = false;
    state.allProductsLoaded = false;
    state.allProducts = [];
    state.sortedProducts = [];
    state.currentSortDirection = null;
    state.displayedCount = 0;

    const loadMoreBtn = document.querySelector(".load-more_btn");
    if (loadMoreBtn && loadMoreBtn.dataset.marketSortHandler) {
      loadMoreBtn.removeEventListener("click", handleLoadMoreClick, true);
      delete loadMoreBtn.dataset.marketSortHandler;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("us_price_sort");
    window.history.replaceState({}, "", url.toString());
  }


  async function handleUSPriceFilter(minPrice, maxPrice) {
    if (!isUSMarket()) {
      return false;
    }

    console.log("US Market price filter activated:", { minPrice, maxPrice });
    showLoading();

    try {
      if (!state.allProductsLoaded || state.allProducts.length === 0) {
        console.log("Fetching all products for filtering...");
        state.allProducts = await fetchAllProducts();
        state.allProductsLoaded = true;
        console.log(`Loaded ${state.allProducts.length} products total`);
      }

      state.currentPriceMin = minPrice;
      state.currentPriceMax = maxPrice;

      state.filteredProducts = applyFiltersAndSort(state.allProducts);
      state.sortedProducts = state.filteredProducts;
      state.displayedCount = 0;

      const perPage = getProductsPerPage();
      renderProducts(state.sortedProducts, perPage, false);

      const url = new URL(window.location.href);
      if (minPrice !== null) {
        url.searchParams.set("us_price_min", minPrice);
      } else {
        url.searchParams.delete("us_price_min");
      }
      if (maxPrice !== null) {
        url.searchParams.set("us_price_max", maxPrice);
      } else {
        url.searchParams.delete("us_price_max");
      }
      url.searchParams.set("us_price_filter", "true");
      window.history.replaceState({}, "", url.toString());

      state.isMarketPriceFilterActive = true;
      state.isMarketPriceSortActive = true; 

      updateProductCount(state.sortedProducts.length);

      console.log(
        `Displaying ${state.displayedCount} of ${state.sortedProducts.length} products (filtered by US price)`
      );
    } catch (error) {
      console.error("Error during US price filter:", error);
      return false;
    } finally {
      hideLoading();
    }

    return true;
  }

  function updateProductCount(count) {
    const countElements = document.querySelectorAll(
      "#ProductCount, #ProductCountDesktop"
    );
    countElements.forEach((el) => {
      if (count > 0) {
        el.textContent = `${count} products`;
      } else {
        el.textContent = "No products found";
      }
    });
  }


  function resetPriceFilter() {
    state.isMarketPriceFilterActive = false;
    state.currentPriceMin = null;
    state.currentPriceMax = null;
    state.filteredProducts = [];

    const minInputs = document.querySelectorAll(".us-price-filter__min");
    const maxInputs = document.querySelectorAll(".us-price-filter__max");
    minInputs.forEach((input) => (input.value = ""));
    maxInputs.forEach((input) => (input.value = ""));

    const url = new URL(window.location.href);
    url.searchParams.delete("us_price_min");
    url.searchParams.delete("us_price_max");
    url.searchParams.delete("us_price_filter");
    window.history.replaceState({}, "", url.toString());

    if (!state.currentSortDirection) {
      window.location.reload();
    } else {
      state.sortedProducts = sortProducts(
        state.allProducts,
        state.currentSortDirection
      );
      state.displayedCount = 0;
      const perPage = getProductsPerPage();
      renderProducts(state.sortedProducts, perPage, false);
      updateProductCount(state.sortedProducts.length);
    }
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }


  function setupPriceFilterListeners() {
    document.addEventListener("keydown", async (event) => {
      if (
        event.key === "Enter" &&
        (event.target.classList.contains("us-price-filter__min") ||
          event.target.classList.contains("us-price-filter__max"))
      ) {
        event.preventDefault();

        const minInput = document.querySelector(".us-price-filter__min");
        const maxInput = document.querySelector(".us-price-filter__max");

        const minValue = minInput?.value
          ? parseFloat(minInput.value.replace(/[^0-9.]/g, ""))
          : null;
        const maxValue = maxInput?.value
          ? parseFloat(maxInput.value.replace(/[^0-9.]/g, ""))
          : null;

        if (minValue !== null || maxValue !== null) {
          await handleUSPriceFilter(minValue, maxValue);
        }
      }
    });

    document.addEventListener("click", (event) => {
      if (
        event.target.classList.contains("us-price-filter__reset") ||
        event.target.closest(".us-price-filter__reset")
      ) {
        event.preventDefault();
        resetPriceFilter();
      }
    });

    document.addEventListener("click", async (event) => {
      const applyButton = event.target.closest(
        ".facets__apply, .mobile-facets__apply"
      );
      if (!applyButton) return;

      const usPriceFilterDetails = applyButton.closest(".us-price-filter");
      const isMobileDrawerApply =
        applyButton.classList.contains("mobile-facets__apply") &&
        !applyButton.classList.contains("facets__apply");

      if (!usPriceFilterDetails && !isMobileDrawerApply) return;

      let minInput;
      let maxInput;

      if (usPriceFilterDetails) {
        minInput = usPriceFilterDetails.querySelector(".us-price-filter__min");
        maxInput = usPriceFilterDetails.querySelector(".us-price-filter__max");
      } else {
        minInput = document.querySelector("#Mobile-Filter-us-price-GTE");
        maxInput = document.querySelector("#Mobile-Filter-us-price-LTE");
      }

      if (!minInput && !maxInput) return;

      const minValue = minInput?.value
        ? parseFloat(minInput.value.replace(/[^0-9.]/g, ""))
        : null;
      const maxValue = maxInput?.value
        ? parseFloat(maxInput.value.replace(/[^0-9.]/g, ""))
        : null;

      if (minValue !== null || maxValue !== null) {
        const desktopMinInput = document.querySelector("#Filter-us-price-GTE");
        const desktopMaxInput = document.querySelector("#Filter-us-price-LTE");
        if (desktopMinInput && minInput) {
          desktopMinInput.value = minInput.value;
        }
        if (desktopMaxInput && maxInput) {
          desktopMaxInput.value = maxInput.value;
        }

        await handleUSPriceFilter(minValue, maxValue);
      }
    });
  }
  async function checkInitialPriceFilter() {
    const url = new URL(window.location.href);
    const usPriceFilter = url.searchParams.get("us_price_filter");
    const minPrice = url.searchParams.get("us_price_min");
    const maxPrice = url.searchParams.get("us_price_max");

    if (isUSMarket() && usPriceFilter === "true" && (minPrice || maxPrice)) {
      const minValue = minPrice ? parseFloat(minPrice) : null;
      const maxValue = maxPrice ? parseFloat(maxPrice) : null;

      setTimeout(() => {
        const minInputs = document.querySelectorAll(".us-price-filter__min");
        const maxInputs = document.querySelectorAll(".us-price-filter__max");
        minInputs.forEach((input) => (input.value = minValue || ""));
        maxInputs.forEach((input) => (input.value = maxValue || ""));
      }, 100);

      setTimeout(async () => {
        await handleUSPriceFilter(minValue, maxValue);
      }, 500);
    }
  }

  function interceptSortForms() {
    document.addEventListener(
      "change",
      async (event) => {
        const target = event.target;

        if (target.name !== "sort_by") return;

        const sortValue = target.value;

        if (isUSMarket() && isPriceSort(sortValue)) {
          event.preventDefault();
          event.stopPropagation();

          const handled = await handleUSPriceSort(sortValue);
          if (handled) {
            const mobileDrawer = target.closest("menu-drawer");
            if (mobileDrawer) {
              const summary = mobileDrawer.querySelector("summary");
              if (summary) summary.click();
            }
            return;
          }
        } else {
          resetSort();
        }
      },
      true
    );
  }

  async function checkInitialSort() {
    const url = new URL(window.location.href);
    const sortBy = url.searchParams.get("sort_by");
    const usPriceSort = url.searchParams.get("us_price_sort");

    if (isUSMarket() && isPriceSort(sortBy)) {
      setTimeout(async () => {
        await handleUSPriceSort(sortBy);
      }, 500);
    }
  }

  /**
   * Inject skeleton card CSS styles
   */
  function injectStyles() {
    if (document.getElementById("market-price-sort-styles")) return;

    const styles = document.createElement("style");
    styles.id = "market-price-sort-styles";
    styles.textContent = `
      .skeleton-card {
        opacity: 1;
      }
      
      .skeleton-card__inner {
        background: var(--color-background, #fff);
        border-radius: 4px;
        overflow: hidden;
      }
      
      .skeleton-card__image {
        width: 100%;
        padding-bottom: 125%; /* Match your product card aspect ratio */
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        border-radius: 4px;
      }
      
      .skeleton-card__content {
        padding: 12px 0;
      }
      
      .skeleton-card__title {
        height: 16px;
        width: 80%;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        border-radius: 4px;
        margin-bottom: 8px;
      }
      
      .skeleton-card__price {
        height: 14px;
        width: 40%;
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200% 100%;
        border-radius: 4px;
      }
      
      .skeleton-animation {
        animation: skeleton-shimmer 1.5s infinite;
      }
      
      @keyframes skeleton-shimmer {
        0% {
          background-position: 200% 0;
        }
        100% {
          background-position: -200% 0;
        }
      }
      
      /* Fade in animation for loaded products */
      .product-card-fade-in {
        animation: productFadeIn 0.3s ease-out;
      }
      
      @keyframes productFadeIn {
        from {
          opacity: 0;
          transform: translateY(10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(styles);
  }

  function init() {
    if (!document.getElementById("product-grid")) return;

    injectStyles();

    console.log(
      "Market Price Sort/Filter initialized. US Market:",
      isUSMarket()
    );

    interceptSortForms();

    if (isUSMarket()) {
      setupPriceFilterListeners();
      checkInitialPriceFilter();

      document.addEventListener("products:loaded", async () => {
        if (state.isReapplyingFilter) {
          return;
        }

        if (
          state.isMarketPriceFilterActive &&
          (state.currentPriceMin !== null || state.currentPriceMax !== null)
        ) {
          console.log("Re-applying US price filter after facet update");

          state.isReapplyingFilter = true;

          setTimeout(async () => {
            const minInputs = document.querySelectorAll(
              ".us-price-filter__min"
            );
            const maxInputs = document.querySelectorAll(
              ".us-price-filter__max"
            );
            minInputs.forEach(
              (input) => (input.value = state.currentPriceMin || "")
            );
            maxInputs.forEach(
              (input) => (input.value = state.currentPriceMax || "")
            );

            state.allProductsLoaded = false;
            state.allProducts = [];

            await handleUSPriceFilter(
              state.currentPriceMin,
              state.currentPriceMax
            );

            state.isReapplyingFilter = false;
          }, 100);
        }
      });
    }

    checkInitialSort();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.MarketPriceSort = {
    isUSMarket,
    handleUSPriceSort,
    handleUSPriceFilter,
    resetSort,
    resetPriceFilter,
    getState: () => ({ ...state }),
  };
})();
